import type { ModalIconVariant } from '@components/ModalIcon';
import { getResolvedConfig, getTransitionConfig } from '@ContractBuilder/components/Modal/ConfirmStatusModal/helpers';
import type { ResourceState } from '@domain/schemas/contract.schema';
import type { DocumentMode } from '@helpers/document-mode';
import { getFormattedStatus } from '@root/helpers';

export type Config = {
  title: string;
  descriptions: string[];
  icon: ModalIconVariant;
  buttons: {
    text: string;
    type: 'secondary' | 'primary';
    handleClick: () => void;
    disableWhenLoading: boolean;
  }[];
  includeValidationChecks: boolean;
  includeFooter?: boolean;
};

type ConfigProps = {
  isDocValid: boolean;
  documentMode: DocumentMode;
  hasMissingDataPoints: boolean;
  hasNoMaterialChanges: boolean;
  status: ResourceState | undefined;
  targetStatus: ResourceState;
  onClose: () => void;
  onSubmit: () => void;
  onSubmitByPassValidation: () => void;
};

export const getConfig = ({
  isDocValid,
  documentMode,
  hasMissingDataPoints,
  hasNoMaterialChanges,
  status,
  targetStatus,
  onClose,
  onSubmit,
  onSubmitByPassValidation,
}: ConfigProps): Config | null => {
  const dismiss = {
    text: `Ok, return to ${getFormattedStatus(status ?? 'REVIEW')}`,
    type: 'primary',
    handleClick: onClose,
    disableWhenLoading: false,
  } as const;

  if (!isDocValid) {
    return {
      title: 'There are issues with the contract',
      descriptions: [
        'The contract is incomplete and has failed validation. Please return it to Draft and make the necessary updates to proceed.',
      ],
      icon: 'danger',
      buttons: [dismiss],
      includeValidationChecks: true,
    };
  }

  if (hasMissingDataPoints) {
    return {
      title: 'Are you sure you are ready to proceed?',
      descriptions: [
        'Some mandatory datapoints have been populated but are missing from the contract. Please return the contract to Draft and ensure all required data points are present to maintain data integrity.',
      ],
      icon: 'warning',
      buttons: [
        {
          text: 'Bypass validation',
          type: 'secondary',
          handleClick: onSubmitByPassValidation,
          disableWhenLoading: true,
        },
        dismiss,
      ],
      includeValidationChecks: true,
      includeFooter: true,
    };
  }

  if (hasNoMaterialChanges) {
    return {
      title: 'Are you sure you are ready to proceed?',
      descriptions: [
        'There appear to be no material changes on this endorsement. We recommend returning it to Draft and confirming all changes have been included correctly to maintain data integrity.',
        'If you proceed, there are no material changes to merge for this endorsement.',
      ],
      icon: 'warning',
      buttons: [
        {
          text: 'Bypass validation',
          type: 'secondary',
          handleClick: onSubmit,
          disableWhenLoading: true,
        },
        dismiss,
      ],
      includeValidationChecks: false,
    };
  }

  const context = { fromStatus: status, toStatus: targetStatus, documentMode };
  const transition = getTransitionConfig(status, targetStatus, documentMode);

  if (!transition) return null;

  const { title, content, confirmText } = getResolvedConfig(transition, context);

  return {
    title,
    descriptions: [content],
    icon: transition.icon?.type ?? 'valid',
    buttons: [
      ...(transition.buttons.cancel
        ? [{ text: 'Cancel', type: 'secondary' as const, handleClick: onClose, disableWhenLoading: false }]
        : []),
      {
        text: confirmText,
        type: 'primary',
        handleClick: onSubmit,
        disableWhenLoading: true,
      },
    ],
    includeValidationChecks: true,
  };
};
